import { useEffect, useState } from 'react';

/**
 * Jednorázové načtení Google Maps JS API (places pro našeptávač adres
 * ve výběru poboček). Bez klíče vrací ready=false a modal se degraduje
 * na fulltextové filtrování seznamu.
 */
let loadingPromise: Promise<void> | null = null;

declare global {
    interface Window {
        google?: any;
    }
}

function loadScript(apiKey: string, language: string): Promise<void> {
    if (window.google?.maps?.places) {
        return Promise.resolve();
    }

    if (!loadingPromise) {
        loadingPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&language=${encodeURIComponent(language)}`;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                loadingPromise = null;
                reject(new Error('Google Maps failed to load'));
            };
            document.head.appendChild(script);
        });
    }

    return loadingPromise;
}

/**
 * Lazy load legacy MarkerClusterer knihovny (js/custom/markerclusterer.js —
 * stejná, kterou používá legacy googleMap.js). NULL když se nepovede načíst;
 * markery se pak vykreslí bez clusteringu.
 */
let clustererPromise: Promise<any | null> | null = null;

export function loadMarkerClusterer(): Promise<any | null> {
    if ((window as any).MarkerClusterer) {
        return Promise.resolve((window as any).MarkerClusterer);
    }

    if (!clustererPromise) {
        clustererPromise = new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = '/js/custom/markerclusterer.js?v=2';
            script.async = true;
            script.onload = () => resolve((window as any).MarkerClusterer ?? null);
            script.onerror = () => {
                clustererPromise = null;
                resolve(null);
            };
            document.head.appendChild(script);
        });
    }

    return clustererPromise;
}

export function useGoogleMaps(apiKey: string | null, language: string): boolean {
    const [ready, setReady] = useState<boolean>(() => Boolean(window.google?.maps?.places));

    useEffect(() => {
        if (!apiKey || ready) {
            return;
        }

        let cancelled = false;
        loadScript(apiKey, language)
            .then(() => {
                if (!cancelled) setReady(true);
            })
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [apiKey, language, ready]);

    return ready;
}
